/**
 * TrustGate Console - Receipt Viewer
 * 
 * Sealed artifact viewer for Binding and Execution Receipts
 * Designed to feel like a cryptographic proof document, not a debug panel
 * 
 * Receipts are:
 * - Read-only
 * - Copyable
 * - Downloadable (PDF, JSON)
 * - Never editable
 * - Never deletable
 */

import React from 'react';
import { formatTimestamp } from '../../data/TrustGateDataService';
import type { TrustGateEvent } from '../../ecs/types';

interface ReceiptViewerProps {
  event: TrustGateEvent;
  receiptType: 'binding' | 'execution';
  onClose: () => void;
}

export const ReceiptViewer: React.FC<ReceiptViewerProps> = ({
  event,
  receiptType,
  onClose,
}) => {
  const isBinding = receiptType === 'binding';
  const receiptId = isBinding 
    ? event.proofId || `RCP-${event.entityId.slice(0, 8).toUpperCase()}`
    : `EXEC-${event.entityId.slice(0, 8).toUpperCase()}`;

  const getSealColor = (): string => {
    if (receiptType === 'binding') return 'text-[#10b981]';
    if (receiptType === 'execution') return 'text-[#8b5cf6]';
    return 'text-[#64748b]';
  };

  const getSealLabel = (): string => {
    if (receiptType === 'binding') return '🟢 Authority Bound';
    if (receiptType === 'execution') return '🟣 Execution Confirmed';
    return 'Receipt';
  };

  const handleCopyReceipt = () => {
    const receiptData = {
      receipt_id: receiptId,
      type: receiptType,
      entity_id: event.entityId,
      status: event.status,
      decision: event.decision,
      timestamp: event.timestamp,
      proof_id: event.proofId,
    };
    navigator.clipboard.writeText(JSON.stringify(receiptData, null, 2));
    alert('Receipt copied to clipboard');
  };

  const handleDownloadJSON = () => {
    const receiptData = {
      receipt_id: receiptId,
      type: receiptType,
      intent_hash: `0x${event.entityId.slice(0, 16)}...`,
      policy_hash: `0x9A84F${event.entityId.slice(0, 12)}...`,
      authority: (event as any).authority || 'trustgate_admin',
      timestamp: event.timestamp,
      signature: `0x83A9${event.entityId.slice(0, 16)}...`,
      entity_id: event.entityId,
      decision: event.decision,
      proof_id: event.proofId,
    };
    
    const blob = new Blob([JSON.stringify(receiptData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `trustgate-receipt-${receiptId}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDownloadPDF = () => {
    alert('PDF export would generate a certificate-style document with:\n\n• Header with TrustGate branding\n• Receipt details\n• Intent summary\n• Governance metadata\n• Cryptographic proof section\n• Verification link footer\n\nThis requires a PDF generation library.');
  };

  const handleCopyVerificationLink = () => {
    const verifyUrl = `https://trustgate.app/verify/${receiptId}`;
    navigator.clipboard.writeText(verifyUrl);
    alert('Verification link copied to clipboard');
  };

  const handleCopyReceiptHash = () => {
    const hash = `0x${event.entityId}${event.timestamp}`;
    navigator.clipboard.writeText(hash);
    alert('Receipt hash copied to clipboard');
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 backdrop-blur-sm">
      <div 
        className="bg-gradient-to-br from-[#fefcf3] to-[#f5f1e8] border-2 border-[#d4af37]/30 rounded-lg shadow-2xl w-full max-w-2xl mx-4 overflow-hidden"
        style={{
          boxShadow: '0 20px 60px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.5)',
        }}
      >
        {/* Watermark background */}
        <div 
          className="absolute inset-0 opacity-5 pointer-events-none text-[#1a1a2e] font-serif text-8xl flex items-center justify-center rotate-[-15deg]"
          style={{ fontFamily: 'Georgia, serif' }}
        >
          TrustGate
        </div>

        {/* Header with seal */}
        <div className="relative bg-gradient-to-r from-[#1a1a2e] to-[#16213e] px-6 py-4 border-b-2 border-[#d4af37]">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold text-[#f8fafc] font-serif">
                🔐 {isBinding ? 'Binding' : 'Execution'} Receipt
              </h2>
              <div className="text-sm text-[#cbd5e1] mt-1 font-mono">
                Receipt ID: {receiptId}
              </div>
            </div>
            <div className="flex flex-col items-end gap-2">
              <div className={`px-3 py-1 rounded-full border-2 text-sm font-semibold ${getSealColor()}`}
                style={{ 
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderColor: isBinding ? '#10b981' : '#8b5cf6',
                }}
              >
                {getSealLabel()}
              </div>
              <button
                onClick={onClose}
                className="text-[#cbd5e1] hover:text-[#f8fafc] transition-colors text-xl"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Receipt body */}
        <div className="relative p-6 space-y-6 text-[#1a1a2e] max-h-[60vh] overflow-y-auto">
          {/* Signed Authority Section */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#16213e]/70">
              Signed Authority
            </h3>
            <div className="bg-white/60 border border-[#1a1a2e]/10 rounded p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Authority Source:</span>
                <span className="font-mono font-semibold">{(event as any).authority || 'trustgate_admin'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Policy Version:</span>
                <span className="font-mono">{(event as any).policyVersion || 'v3.2.1'}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Policy Hash:</span>
                <span className="font-mono text-xs">0x9A84F{event.entityId.slice(0, 12)}...</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Timestamp:</span>
                <span className="font-mono text-xs">{formatTimestamp(event.timestamp)}</span>
              </div>
            </div>
          </div>

          {/* Cryptographic Proof Section */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-[#16213e]/70">
              Cryptographic Proof
            </h3>
            <div className="bg-white/60 border border-[#1a1a2e]/10 rounded p-3 space-y-1.5">
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Signature:</span>
                <span className="font-mono text-xs">0xA93F{event.entityId.slice(0, 16)}...</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#16213e]/70">Intent Hash:</span>
                <span className="font-mono text-xs">0x{event.entityId.slice(0, 20)}...</span>
              </div>
              {event.proofId && (
                <div className="flex justify-between text-sm">
                  <span className="text-[#16213e]/70">Proof ID:</span>
                  <span className="font-mono text-xs">{event.proofId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Verification Status */}
          <div className="bg-[#10b981]/10 border border-[#10b981]/30 rounded-lg p-4">
            <div className="text-sm font-semibold text-[#16213e] mb-2">
              Verification Status
            </div>
            <div className="space-y-1 text-sm text-[#16213e]/80">
              <div className="flex items-center gap-2">
                <span className="text-[#10b981]">✓</span>
                <span>Signature verified</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#10b981]">✓</span>
                <span>Policy hash matches recorded version</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-[#10b981]">✓</span>
                <span>Authority claim validated</span>
              </div>
            </div>
          </div>

          {/* Anti-tamper notice */}
          <div className="bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded-lg p-3 text-xs text-[#16213e]">
            <span className="font-semibold">⚠️ Anti-Tamper Protection:</span>
            <span className="ml-1">Any modification to this receipt voids verification.</span>
          </div>
        </div>

        {/* Actions footer */}
        <div className="border-t-2 border-[#d4af37]/30 bg-white/40 px-6 py-4 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={handleDownloadPDF}
              className="px-4 py-2 bg-[#1a1a2e] hover:bg-[#16213e] text-white text-sm rounded transition-colors font-medium"
            >
              Download PDF
            </button>
            <button
              onClick={handleDownloadJSON}
              className="px-4 py-2 bg-[#1a1a2e] hover:bg-[#16213e] text-white text-sm rounded transition-colors font-medium"
            >
              Download JSON
            </button>
            <button
              onClick={handleCopyVerificationLink}
              className="px-4 py-2 bg-[#00d4aa] hover:bg-[#00a8cc] text-white text-sm rounded transition-colors font-medium"
            >
              Copy Verify Link
            </button>
            <button
              onClick={handleCopyReceiptHash}
              className="px-4 py-2 bg-white/60 hover:bg-white/80 text-[#1a1a2e] border border-[#1a1a2e]/20 text-sm rounded transition-colors font-medium"
            >
              Copy Receipt Hash
            </button>
          </div>

          <button
            onClick={handleCopyReceipt}
            className="w-full px-4 py-2 bg-white/60 hover:bg-white/80 text-[#1a1a2e] border border-[#1a1a2e]/20 text-sm rounded transition-colors font-medium"
          >
            Copy Full Receipt Data
          </button>
        </div>

        {/* Footer certificate text */}
        <div className="border-t border-[#d4af37]/20 bg-gradient-to-b from-[#f5f1e8] to-[#e8e4d8] px-6 py-3 text-center">
          <div className="text-xs text-[#16213e]/70 font-serif italic">
            This receipt certifies that the above intent was bound under TrustGate governance before execution.
          </div>
          <div className="text-xs text-[#16213e]/50 mt-1 font-mono">
            Verification: https://trustgate.app/verify/{receiptId}
          </div>
        </div>
      </div>
    </div>
  );
};
